/**
 * AIS target overlay — each vessel drawn as an oriented outline sized and shaped
 * to its type (a small target reads small, a big ship reads big; fishing, sailing
 * and big commercial traffic get distinct outlines), with a speed-proportional
 * course/speed vector ahead of it (how far it runs in the chosen time-ahead).
 * Targets flagged as a CPA threat draw red; the tapped one gets a highlight ring.
 *
 * Targets are also aged by how long since we last heard from them. The icon
 * colour drains smoothly from its live category colour toward grey as the last
 * report gets older (fadeToGrey/ageGreyFrac), reaching full grey at the lost
 * threshold, so age shows in the colour rather than snapping at a stage line. On
 * top of that it DIMS in steps as a warning, a lost one gets a cross through it
 * (the plotter "don't trust this" mark), and it's removed at the drop cutoff.
 * A CPA threat overrides the fade and draws red however old it is. Lost targets
 * are dropped from CPA upstream, so a ghost never draws red here.
 * Mirrors the ensure/update layer pattern the weather and tide overlays use.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDestination } from '@/nav';
import type { LngLat } from './aisVectorClip';
import type { AisTarget } from '@/store/ais';
import {
  vesselSymbol,
  vectorLengthNm,
  aisSymbolScaleForZoom,
  CATEGORY_COLOR,
} from '@/ais/vesselSymbol';
import { classifyTarget, targetAgeMs, ageGreyFrac, type AgeStatus } from '@/ais/targetAge';
import { raiseOwnshipLayers } from './ownshipLayer';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'ais-targets';
// Neutral colour for the course/speed vector line (a course predictor, kept one
// tone rather than tinted per type so it never competes with the vessel icon).
const NORMAL = '#1f9e89';
const THREAT = '#e23b3b';
// The grey a target fades to as it ages: a fully-aged (lost) contact reads neutral
// grey regardless of its type, so "old" always reads as old and never as, say, a
// live tanker. Fresh targets show their category colour; the fade runs between.
const LOST_GREY = '#9aa0a6';
// Below this the vessel is effectively stopped, so we draw no vector at all.
const MIN_VECTOR_NM = 0.02;

/** Parse a #rrggbb string to its three 0..255 channels. */
function hexRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
const GREY_RGB = hexRgb(LOST_GREY);

/**
 * Blend a category colour toward grey by `frac` (0 = full colour, 1 = full grey).
 * This is the age fade: the older a target's last report, the greyer its icon, so
 * a contact drains from its live colour to grey as we stop hearing from it rather
 * than snapping at a stage line. Done here in JS (not a map expression) so we can
 * feed the exact per-target fraction and keep it unit-testable.
 */
function fadeToGrey(hex: string, frac: number): string {
  const f = frac <= 0 ? 0 : frac >= 1 ? 1 : frac;
  const [r, g, b] = hexRgb(hex);
  const mix = (c: number, grey: number) => Math.round(c + (grey - c) * f);
  const to2 = (v: number) => v.toString(16).padStart(2, '0');
  return `#${to2(mix(r, GREY_RGB[0]))}${to2(mix(g, GREY_RGB[1]))}${to2(mix(b, GREY_RGB[2]))}`;
}

/**
 * Fill colour for the vessel icon: a CPA threat is red (it must shout over its type
 * AND over its age — a dangerous target stays red however old), and everything else
 * takes its per-target faded colour, computed in build() from the category colour
 * and the age fade above.
 */
const AIS_FILL_COLOR: unknown[] = [
  'case',
  ['get', 'threat'],
  THREAT,
  ['get', 'color'],
];

/** Two diagonals across a symbol's bounding box — the "lost target" cross. */
function crossLines(ring: [number, number][]): [number, number][][] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [
    [
      [minX, minY],
      [maxX, maxY],
    ],
    [
      [minX, maxY],
      [maxX, minY],
    ],
  ];
}

/**
 * Truncates a target's course/speed vector at the coast. Given the segment from
 * the target to its projected tip, it returns where that segment first meets
 * land, the tip unchanged if it stays in open water, or null if the target sits
 * on land (so the vector is dropped). Supplied by the map, which has the chart
 * land polygons; omitted (e.g. in tests) means no clipping.
 */
type LandClip = (from: LngLat, to: LngLat) => LngLat | null;

function build(
  targets: AisTarget[],
  threats: Set<number>,
  selected: number | null,
  vectorMin: number,
  nowMs: number,
  scale: number,
  landClip?: LandClip,
): FeatureCollection {
  const features: Feature[] = [];
  for (const t of targets) {
    const sym = vesselSymbol(t, scale);
    const threat = threats.has(t.mmsi);
    const ageMs = targetAgeMs(t.lastMs, nowMs);
    const age: AgeStatus = classifyTarget(t, nowMs);
    // Fade the icon from its category colour toward grey by how old the last report
    // is. Threat targets ignore this and draw red in paint, so danger still shouts.
    const baseColor = CATEGORY_COLOR[sym.category] ?? CATEGORY_COLOR.other;
    const color = fadeToGrey(baseColor, ageGreyFrac(ageMs));
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [sym.ring] },
      properties: {
        kind: 'tri',
        mmsi: t.mmsi,
        threat,
        selected: t.mmsi === selected,
        category: sym.category,
        age,
        color,
      },
    });
    // A lost target gets a cross through it, the plotter "don't trust this" mark.
    if (age === 'lost') {
      features.push({
        type: 'Feature',
        geometry: { type: 'MultiLineString', coordinates: crossLines(sym.ring) },
        properties: { kind: 'cross' },
      });
    }
    // Course/speed vector along COG: faster vessel, longer line; stopped, none.
    // A lost target's course is a guess now, so we stop drawing its vector.
    const vecNm = vectorLengthNm(t.sogKn, vectorMin);
    if (age !== 'lost' && vecNm >= MIN_VECTOR_NM) {
      const tip = rhumbDestination(t.position, t.cogDeg, vecNm);
      // A real vessel won't steam across land, so stop the predictor at the coast.
      // null means the target itself is on land, so we draw no vector at all.
      const end = landClip ? landClip(t.position, tip) : tip;
      if (end) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [t.position, end] },
          properties: { kind: 'vec', threat, age },
        });
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

// Fade a target by age: live solid, stale dimmed, lost faint.
const AGE_FILL_OPACITY = ['match', ['get', 'age'], 'stale', 0.45, 'lost', 0.3, 0.9] as const;
const AGE_LINE_OPACITY = ['match', ['get', 'age'], 'stale', 0.5, 'lost', 0.35, 1] as const;

/**
 * The AIS layer ids, bottom -> top in draw order (and the order we re-seat them
 * in): the course/speed vector, then its two-arrowhead ground-track marker, then
 * the lost-target cross, all under the vessel icon, with the tapped-target
 * selection outline on top.
 */
const AIS_LAYER_IDS = ['ais-vec', 'ais-vec-arrow', 'ais-cross', 'ais-tri', 'ais-tri-outline'] as const;

/**
 * Lift the AIS target layers to the top of the stack, in draw order, so other
 * vessels are never buried. Same race the ownship, route, marks, notes and
 * EBL/VRM overlays hit: the AIS layers are created before the async vector chart
 * cells finish loading, and a later provider switch or palette swap re-adds the
 * opaque cell base fills ABOVE them, so the targets sit under the depth/land fills
 * and never draw even though the store holds them ("Connected - N targets in
 * range" but nothing on the chart). The ownship/route/marks/EBL-VRM overlays pop
 * back because their update path re-raises on every fix; the AIS update only set
 * the data, so once buried the targets stayed hidden. Re-seating on every ensure
 * and every update (AIS ticks often, so this re-seats constantly) climbs them back
 * on top. moveLayer with no `before` moves a layer to the top, so walking
 * bottom -> top leaves the selection outline highest. Missing layers are skipped.
 *
 * The own boat must stay ABOVE other vessels, so after re-seating AIS we re-raise
 * the ownship layers. Ownship re-raises on its own fixes too, but AIS updates far
 * more often, so without this the boat would flicker under the AIS icons between
 * fixes.
 */
export function raiseAisLayers(map: MaplibreMap): void {
  for (const id of AIS_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
  // Keep our own boat on top of the other vessels.
  if (map.getLayer('ownship-boat')) raiseOwnshipLayers(map);
}

export function ensureAisLayer(
  map: MaplibreMap,
  targets: AisTarget[],
  threats: Set<number>,
  selected: number | null,
  vectorMin = 6,
  nowMs = Date.now(),
  palette: Palette = 'day',
): void {
  const p = getPalette(palette);
  const data = build(targets, threats, selected, vectorMin, nowMs, aisSymbolScaleForZoom(map.getZoom()));
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
  }
  if (!map.getLayer('ais-vec')) {
    map.addLayer({
      id: 'ais-vec',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'vec'],
      paint: {
        'line-color': ['case', ['get', 'threat'], THREAT, NORMAL],
        'line-width': 1.4,
        'line-opacity': AGE_LINE_OPACITY as unknown as maplibregl.ExpressionSpecification,
      },
    });
  }
  if (!map.getLayer('ais-tri')) {
    map.addLayer({
      id: 'ais-tri',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'tri'],
      paint: {
        'fill-color': AIS_FILL_COLOR as unknown as maplibregl.ExpressionSpecification,
        'fill-opacity': AGE_FILL_OPACITY as unknown as maplibregl.ExpressionSpecification,
      },
    });
  }
  // Two arrowheads along the target's vector — the RYA ground-track symbol (one
  // arrowhead = water track, two = ground track, three = tidal stream). AIS gives
  // COG/SOG, a track OVER THE GROUND, so two arrowheads is the correct mark, and it
  // matches the own-ship COG/SOG vector. Placed at the line centre like the boat's,
  // sized a touch smaller than own-ship's since the target vector is shorter, with a
  // UI_BG halo so the chevrons stay legible over busy chart detail in any palette.
  if (!map.getLayer('ais-vec-arrow')) {
    map.addLayer({
      id: 'ais-vec-arrow',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'vec'],
      layout: {
        'symbol-placement': 'line-center',
        'text-field': '>>',
        'text-font': ['Open Sans Regular'],
        'text-size': 13,
        'text-rotation-alignment': 'map',
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': ['case', ['get', 'threat'], THREAT, NORMAL],
        'text-halo-color': p.UI_BG,
        'text-halo-width': 1.3,
        'text-opacity': AGE_LINE_OPACITY as unknown as maplibregl.ExpressionSpecification,
      },
    });
  }
  if (!map.getLayer('ais-cross')) {
    map.addLayer({
      id: 'ais-cross',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cross'],
      paint: { 'line-color': '#c8c8c8', 'line-width': 1.4, 'line-opacity': 0.85 },
    });
  }
  if (!map.getLayer('ais-tri-outline')) {
    map.addLayer({
      id: 'ais-tri-outline',
      type: 'line',
      source: SOURCE_ID,
      filter: ['all', ['==', ['get', 'kind'], 'tri'], ['get', 'selected']],
      paint: { 'line-color': '#ffffff', 'line-width': 1.8 },
    });
  }
  // Whether we just created them or they already existed (made before the async
  // cells loaded), keep the targets above the cell fills so nothing hides behind
  // the vector chart.
  raiseAisLayers(map);
}

export function updateAisLayer(
  map: MaplibreMap,
  targets: AisTarget[],
  threats: Set<number>,
  selected: number | null,
  vectorMin = 6,
  nowMs = Date.now(),
  landClip?: LandClip,
): void {
  const src = map.getSource(SOURCE_ID);
  if (src)
    (src as maplibregl.GeoJSONSource).setData(
      build(targets, threats, selected, vectorMin, nowMs, aisSymbolScaleForZoom(map.getZoom()), landClip),
    );
  // Re-seat on every update (fires on each throttled AIS/own-ship tick), so the
  // targets climb back above any cell/base layers re-added by a provider, symbol-set
  // or palette change since they were created - the defence the ownship layer gets
  // on every fix.
  raiseAisLayers(map);
}

/** Hide/show all AIS layers (when the source is off). */
export function setAisLayersVisible(map: MaplibreMap, visible: boolean): void {
  for (const id of ['ais-vec', 'ais-vec-arrow', 'ais-tri', 'ais-cross', 'ais-tri-outline']) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  }
}
